import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useCRM } from '../../contexts/CRMContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useFeatureRegistry } from '../../contexts/FeatureRegistryContext';
import { adminSystemAPI } from '../../utils/crmApi';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { SmartDashboardCard } from '../ui/help-components';
import { EnhancedBarChart, EnhancedLineChart } from '../ui/enhanced-charts';
import { Pill, StatusPill } from '../kibo-ui/pill';
import {
  TableProvider,
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
  TableSearch,
  TableFooter,
  useTable,
} from '../kibo-ui/table';
import {
  BuildingOffice2Icon,
  UserGroupIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  ShieldCheckIcon,
  ClockIcon,
  ChevronDownIcon,
  EyeIcon,
  TrashIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

const PLAN_OPTIONS = [
  { value: 'trial', label: 'Trial', shortLabel: 'Trial' },
  { value: 'starter', label: 'Starter', shortLabel: 'Starter' },
  { value: 'professional', label: 'Professional', shortLabel: 'Pro' },
  { value: 'enterprise', label: 'Enterprise', shortLabel: 'Enterprise' },
];

const FEATURE_STATUS_OPTIONS = [
  { value: 'live', label: 'Live' },
  { value: 'beta', label: 'Beta' },
  { value: 'new', label: 'New' },
  { value: 'hidden', label: 'Hidden' },
];

const PLAN_ORDER = ['trial', 'starter', 'professional', 'enterprise'];
const PLAN_LABELS = {
  trial: 'Trial',
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
};

const FEATURE_STATUS_VARIANT = {
  live: 'success',
  beta: 'warning',
  new: 'info',
  hidden: 'default',
};

const formatNumber = (value) => {
  if (typeof value !== 'number') return value ?? '-';
  return value.toLocaleString();
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (_) {
    return value;
  }
};

const booleanFeatureEntries = (planDefaults = {}, effectiveFlags = {}) => {
  const keys = new Set([
    ...Object.keys(planDefaults),
    ...Object.keys(effectiveFlags),
  ]);

  return Array.from(keys)
    .filter((key) => typeof planDefaults[key] === 'boolean' || typeof effectiveFlags[key] === 'boolean')
    .sort();
};

export default function AdminDashboard() {
  const { user, apiCall } = useCRM();
  const { refresh: refreshGlobalRegistry, features: featureRegistryContext } = useFeatureRegistry();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [busy, setBusy] = useState({});
  const [featureRegistry, setFeatureRegistry] = useState({});
  const [featureDefinitions, setFeatureDefinitions] = useState([]);
  const [limitDrafts, setLimitDrafts] = useState({});
  const [globalFeatureDraft, setGlobalFeatureDraft] = useState({});
  const [confirmGlobalOpen, setConfirmGlobalOpen] = useState(false);
  const [pendingGlobalChanges, setPendingGlobalChanges] = useState([]);
  const [globalSaving, setGlobalSaving] = useState(false);
  const [newFeatureKey, setNewFeatureKey] = useState('');
  const [newFeatureStatus, setNewFeatureStatus] = useState('hidden');
  const [trialInterestEntries, setTrialInterestEntries] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [agencySearch, setAgencySearch] = useState('');
  const [openAgencies, setOpenAgencies] = useState({});
  const [featureStatusFilter, setFeatureStatusFilter] = useState('all');
  const [featureInfoDialog, setFeatureInfoDialog] = useState({
    open: false,
    featureKey: '',
    description: '',
    defaultStatus: '',
    plans: {},
    hasPlanData: false,
  });
  const [libraryTemplates, setLibraryTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateError, setTemplateError] = useState(null);
  const [templateFormOpen, setTemplateFormOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    id: null,
    type: 'task',
    slug: '',
    title: '',
    description: '',
    category: '',
    tags: '',
    estimatedDuration: '',
    stepsJson: '[]',
    contentJson: '{}',
    changeNotes: ''
  });
  const [templateOriginalState, setTemplateOriginalState] = useState(null); // Track original state for change detection
  const [templateSaving, setTemplateSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, confirmLabel: 'Confirm', variant: 'default' });
  const [promptDialog, setPromptDialog] = useState({ open: false, title: '', message: '', defaultValue: '', onConfirm: null, inputLabel: 'Value' });
  const [publishState, setPublishState] = useState({
    open: false,
    templateId: null,
    highlightDays: 7,
    highlightLabel: 'New',
    notifyUsers: true,
    severity: 'info',
    announcementTitle: '',
    announcementMessage: '',
    expiresInDays: 7,
    changeNotes: ''
  });
  const [systemConfigDraft, setSystemConfigDraft] = useState({
    banner: { enabled: false, message: '', severity: 'info' },
    maintenance: { enabled: false, message: '' }
  });
  const [systemConfigLoading, setSystemConfigLoading] = useState(false);
  const [systemConfigSaving, setSystemConfigSaving] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [announcementDraft, setAnnouncementDraft] = useState({
    title: '',
    message: '',
    severity: 'info',
    expiresInDays: 7
  });
  const [announcementSaving, setAnnouncementSaving] = useState(false);
  const [announcementFormOpen, setAnnouncementFormOpen] = useState(false);
  const [templateTypeSelectorOpen, setTemplateTypeSelectorOpen] = useState(false);
  const [deleteTemplateDialog, setDeleteTemplateDialog] = useState({ open: false, templateId: null, templateTitle: '' });
  const [users, setUsers] = useState([]);
  const [totalClients, setTotalClients] = useState(0);
  const [userFilters, setUserFilters] = useState({
    trial: 'all', // 'all', 'yes', 'no'
    plan: 'all', // 'all', 'trial', 'starter', 'professional', 'enterprise'
    status: 'all', // 'all', 'verified', 'unverified'
    role: 'all', // 'all', 'admin', 'member', 'client'
  });

  const isSuperadmin = user?.isSuperadmin;

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [overviewRes, agenciesRes, featuresRes, trialRes, usersRes] = await Promise.all([
        apiCall('/api/crm/admin/overview'),
        apiCall('/api/crm/admin/agencies'),
        apiCall('/api/crm/admin/features'),
        apiCall('/api/crm/admin/trial-interest?limit=200'),
        apiCall('/api/crm/admin/users'),
      ]);

      setOverview(overviewRes?.data || null);
      const agencyPayload = Array.isArray(agenciesRes?.data?.agencies)
        ? agenciesRes.data.agencies
        : Array.isArray(agenciesRes?.data)
          ? agenciesRes.data
          : [];
      setAgencies(agencyPayload);

      const registry = featuresRes?.data?.featureRegistry || agenciesRes?.data?.featureRegistry || {};
      const definitions = featuresRes?.data?.featureDefinitions || [];
      setFeatureDefinitions(definitions);
      setFeatureRegistry(registry);
      const initialDraft = {};
      definitions.forEach((def) => {
        initialDraft[def.key] = registry?.[def.key]?.status ?? def.defaultStatus ?? 'hidden';
      });
      Object.keys(registry || {}).forEach((key) => {
        if (!(key in initialDraft)) {
          initialDraft[key] = registry[key]?.status ?? 'hidden';
        }
      });
      Object.keys(featureRegistryContext || {}).forEach((key) => {
        if (!(key in initialDraft)) {
          initialDraft[key] = featureRegistryContext[key]?.status ?? 'hidden';
        }
      });
      setGlobalFeatureDraft(initialDraft);

      setTrialInterestEntries(trialRes?.data?.entries || trialRes?.data || []);
      setUsers(usersRes?.data?.users || []);
      setTotalClients(usersRes?.data?.totalClients || 0);
    } catch (err) {
      console.error('Failed to load admin dashboard:', err);
      setError(err?.response?.data?.message || err.message || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplateError(null);
    try {
      // Load all template types (task, workflow, form, email)
      const response = await adminSystemAPI.getTemplates();
      if (response.data?.success) {
        setLibraryTemplates(response.data.data || []);
      } else {
        setLibraryTemplates([]);
      }
    } catch (err) {
      console.error('Failed to load global templates:', err);
      setTemplateError(err);
      setLibraryTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  const loadSystemConfig = useCallback(async () => {
    setSystemConfigLoading(true);
    try {
      const response = await adminSystemAPI.getConfig();
      if (response.data?.success) {
        const draft = {
          banner: response.data.data?.banner || { enabled: false, message: '', severity: 'info' },
          maintenance: response.data.data?.maintenance || { enabled: false, message: '' }
        };
        setSystemConfigDraft(draft);
      }
    } catch (err) {
      console.error('Failed to load system configuration:', err);
      toast.error('Failed to load system configuration');
    } finally {
      setSystemConfigLoading(false);
    }
  }, []);

  const loadAnnouncements = useCallback(async () => {
    setAnnouncementsLoading(true);
    try {
      const response = await adminSystemAPI.getAnnouncements({ limit: 25 });
      if (response.data?.success) {
        setAnnouncements(response.data.data || []);
      }
    } catch (err) {
      console.error('Failed to load announcements:', err);
      toast.error('Failed to load announcements');
    } finally {
      setAnnouncementsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperadmin) {
      loadData();
    }
  }, [isSuperadmin]);

  useEffect(() => {
    if (activeTab === 'templates' && isSuperadmin) {
      loadTemplates();
    }
  }, [activeTab, isSuperadmin, loadTemplates]);

  useEffect(() => {
    if (activeTab === 'system' && isSuperadmin) {
      loadSystemConfig();
    }
  }, [activeTab, isSuperadmin, loadSystemConfig]);

  useEffect(() => {
    if (activeTab === 'announcements' && isSuperadmin) {
      loadAnnouncements();
    }
  }, [activeTab, isSuperadmin, loadAnnouncements]);

  useEffect(() => {
    const normalise = (value) => (value === null || value === undefined ? '' : String(value));
    const next = {};
    agencies.forEach((agency) => {
      next[agency.id] = {
        seatLimit: normalise(agency.seatLimit),
        maxAgencies: normalise(agency.maxAgencies),
        maxClients: normalise(agency.maxClients),
        maxAccounts: normalise(agency.maxAccounts),
      };
    });
    setLimitDrafts(next);
    setOpenAgencies({});
  }, [agencies]);

  const handlePlanChange = async (agency, planTier) => {
    setBusy((prev) => ({ ...prev, [agency.id]: true }));
    try {
      const response = await apiCall(`/api/crm/admin/agencies/${agency.id}/plan`, {
        method: 'PATCH',
        data: {
          planTier,
        },
      });
      const updated = response?.data;
      if (updated) {
        setAgencies((prev) =>
          prev.map((item) =>
            item.id === agency.id ? { ...item, ...updated } : item
          )
        );
      }
      toast.success(`Plan updated to ${planTier}`);
    } catch (err) {
      console.error('Plan update failed:', err);
      toast.error(err?.response?.data?.message || 'Failed to update plan');
    } finally {
      setBusy((prev) => ({ ...prev, [agency.id]: false }));
    }
  };

  const handleResetOverrides = async (agency) => {
    setBusy((prev) => ({ ...prev, [agency.id]: true }));
    try {
      const response = await apiCall(`/api/crm/admin/agencies/${agency.id}/plan`, {
        method: 'PATCH',
        data: {
          planTier: agency.planTier,
          resetOverrides: true,
        },
      });
      const updated = response?.data;
      if (updated) {
        setAgencies((prev) =>
          prev.map((item) =>
            item.id === agency.id ? { ...item, ...updated } : item
          )
        );
      }
      toast.success('Feature overrides reset to plan defaults');
    } catch (err) {
      console.error('Reset overrides failed:', err);
      toast.error(err?.response?.data?.message || 'Failed to reset overrides');
    } finally {
      setBusy((prev) => ({ ...prev, [agency.id]: false }));
    }
  };

  const handleFeatureToggle = async (agency, featureKey, nextValue) => {
    const planDefaults = agency.planDefaults || {};
    const defaultValue = planDefaults[featureKey];
    const payloadValue = nextValue === defaultValue ? null : nextValue;

    setBusy((prev) => ({ ...prev, [`${agency.id}:${featureKey}`]: true }));
    try {
      const response = await apiCall(`/api/crm/admin/agencies/${agency.id}/feature-flags`, {
        method: 'PATCH',
        data: {
          overrides: {
            [featureKey]: payloadValue,
          },
        },
      });

      const updated = response?.data;
      if (updated) {
        setAgencies((prev) =>
          prev.map((item) =>
            item.id === agency.id ? { ...item, ...updated } : item
          )
        );
      }
      toast.success(`Feature "${featureKey}" ${nextValue ? 'enabled' : 'disabled'}`);
    } catch (err) {
      console.error('Feature flag update failed:', err);
      toast.error(err?.response?.data?.message || 'Failed to update feature flag');
    } finally {
      setBusy((prev) => ({ ...prev, [`${agency.id}:${featureKey}`]: false }));
    }
  };

  const updateLimitDraft = (agencyId, field, value) => {
    setLimitDrafts((prev) => ({
      ...prev,
      [agencyId]: {
        ...(prev[agencyId] || {}),
        [field]: value
      }
    }));
  };

  const handleSaveLimits = async (agency, customOverrides) => {
    const draft = limitDrafts[agency.id] || {};
    const parseValue = (val) => {
      if (val === '' || val === null || val === undefined) return null;
      const parsed = Number(val);
      return Number.isNaN(parsed) ? null : parsed;
    };
    const overridesPayload = customOverrides || {
      seatLimit: parseValue(draft.seatLimit),
      maxAgencies: parseValue(draft.maxAgencies),
      maxClients: parseValue(draft.maxClients),
      maxAccounts: parseValue(draft.maxAccounts),
    };
    setBusy((prev) => ({ ...prev, [`limits:${agency.id}`]: true }));
    try {
      const response = await apiCall(`/api/crm/admin/agencies/${agency.id}/plan`, {
        method: 'PATCH',
        data: {
          planTier: agency.planTier,
          overrides: overridesPayload,
        },
      });
      const updated = response?.data;
      if (updated) {
        setAgencies((prev) =>
          prev.map((item) =>
            item.id === agency.id ? { ...item, ...updated } : item
          )
        );
      }
      toast.success('Limits updated');
    } catch (err) {
      console.error('Limit update failed:', err);
      toast.error(err?.response?.data?.message || 'Failed to update limits');
    } finally {
      setBusy((prev) => ({ ...prev, [`limits:${agency.id}`]: false }));
    }
  };

  const handleResetLimits = (agency) => {
    setLimitDrafts((prev) => ({
      ...prev,
      [agency.id]: {
        seatLimit: '',
        maxAgencies: '',
        maxClients: '',
        maxAccounts: '',
      }
    }));
    handleSaveLimits(agency, {
      seatLimit: null,
      maxAgencies: null,
      maxClients: null,
      maxAccounts: null,
    });
  };

  const handleCloseConfirm = () => {
    if (globalSaving) return;
    setPendingGlobalChanges([]);
    setConfirmGlobalOpen(false);
  };

  const handleAddGlobalFeature = (event) => {
    event.preventDefault();
    const key = (newFeatureKey || '').trim();
    if (!key) {
      toast.error('Feature key is required');
      return;
    }
    const status = newFeatureStatus || 'hidden';
    setFeatureDefinitions((prev) => {
      if (prev.some((def) => def.key === key)) return prev;
      return [...prev, { key, plans: {}, description: '', defaultStatus: 'hidden' }];
    });
    setGlobalFeatureDraft((prev) => ({
      ...prev,
      [key]: prev?.[key] ?? status
    }));
    setNewFeatureKey('');
    setNewFeatureStatus('hidden');
    toast.success(`Added "${key}" to pending changes. Remember to save.`);
  };

  const isLoading = loading && !agencies.length;

  const featureItems = useMemo(() => {
    const map = new Map();

    featureDefinitions.forEach((def) => {
      map.set(def.key, {
        key: def.key,
        plans: def.plans || {},
        defaultStatus: def.defaultStatus || 'hidden',
        description: def.description || ''
      });
    });

    Object.entries(featureRegistry || {}).forEach(([key, meta]) => {
      if (!map.has(key)) {
        map.set(key, {
          key,
          plans: {},
          defaultStatus: meta?.status || 'hidden',
          description: meta?.description || ''
        });
      } else {
        const item = map.get(key);
        if (!item.description && meta?.description) {
          item.description = meta.description;
        }
      }
    });

    Object.entries(featureRegistryContext || {}).forEach(([key, meta]) => {
      if (!map.has(key)) {
        map.set(key, {
          key,
          plans: {},
          defaultStatus: meta?.status || 'hidden',
          description: meta?.description || ''
        });
      }
    });

    Object.keys(globalFeatureDraft || {}).forEach((key) => {
      if (!map.has(key)) {
        map.set(key, {
          key,
          plans: {},
          defaultStatus: 'hidden',
          description: ''
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
  }, [featureDefinitions, featureRegistry, featureRegistryContext, globalFeatureDraft]);

  const featureBaselineMap = useMemo(() => {
    const map = new Map();
    featureItems.forEach((item) => {
      const baseline = featureRegistry?.[item.key]?.status ?? item.defaultStatus ?? 'hidden';
      map.set(item.key, baseline);
    });
    return map;
  }, [featureItems, featureRegistry]);

  const featureStatusCounts = useMemo(() => {
    const counts = { live: 0, beta: 0, new: 0, hidden: 0 };
    featureBaselineMap.forEach((status) => {
      if (typeof counts[status] !== 'number') {
        counts[status] = 0;
      }
      counts[status] += 1;
    });
    return counts;
  }, [featureBaselineMap]);

  const featureKeyOptions = useMemo(() => featureItems.map((item) => item.key), [featureItems]);

  const filteredFeatureItems = useMemo(() => {
    if (featureStatusFilter === 'all') {
      return featureItems;
    }
    return featureItems.filter((item) => {
      const baseline = featureBaselineMap.get(item.key) ?? 'hidden';
      const draftStatus = globalFeatureDraft[item.key] ?? baseline;
      return draftStatus === featureStatusFilter;
    });
  }, [featureItems, featureStatusFilter, featureBaselineMap, globalFeatureDraft]);

  const totalFeatureCount = featureItems.length;

  const featureFilterOptions = useMemo(() => ([
    { value: 'all', label: `All ${totalFeatureCount}` },
    { value: 'live', label: `Live ${featureStatusCounts.live ?? 0}` },
    { value: 'beta', label: `Beta ${featureStatusCounts.beta ?? 0}` },
    { value: 'new', label: `New ${featureStatusCounts.new ?? 0}` },
    { value: 'hidden', label: `Hidden ${featureStatusCounts.hidden ?? 0}` },
  ]), [totalFeatureCount, featureStatusCounts]);

  const featureTableColumns = useMemo(() => ([
    { accessor: 'key', label: 'Feature' },
    { accessor: 'defaultStatus', label: 'Default' },
    { accessor: 'status', label: 'Global Status' },
    { accessor: 'plans', label: 'Plan Coverage' },
    { accessor: 'actions', label: '' },
  ]), []);

  const featureTableRows = useMemo(() => {
    return filteredFeatureItems.map((item) => {
      const baseline = featureBaselineMap.get(item.key) ?? 'hidden';
      const draftStatus = globalFeatureDraft[item.key] ?? baseline;
      const description =
        featureRegistry?.[item.key]?.description ||
        item.description ||
        featureRegistryContext?.[item.key]?.description ||
        '';
      const plans = item.plans || {};
      const hasPlanData = PLAN_ORDER.some((planId) => typeof plans?.[planId] === 'boolean');

      return {
        key: item.key,
        defaultStatus: item.defaultStatus ?? 'hidden',
        description,
        draftStatus,
        baseline,
        dirty: draftStatus !== baseline,
        plans,
        hasPlanData,
      };
    });
  }, [filteredFeatureItems, featureBaselineMap, globalFeatureDraft, featureRegistry, featureRegistryContext]);

  const planMixChartData = useMemo(() => {
    if (!overview?.agencies?.byPlan) return PLAN_OPTIONS.map(option => ({ plan: option.label, count: 0 }));
    return PLAN_OPTIONS.map(option => ({
      plan: option.shortLabel || option.label,
      count: overview.agencies.byPlan?.[option.value] || 0
    }));
  }, [overview]);

  const trialInterestTrendData = useMemo(() => {
    return (overview?.trialInterest?.trend || []).map(item => ({
      date: item.date,
      total: item.total,
      converted: item.converted,
      unconverted: item.unconverted
    }));
  }, [overview]);

  const trialInterestLineData = useMemo(() => {
    return trialInterestTrendData.map(item => ({
      date: item.date,
      value: item.total,
    }));
  }, [trialInterestTrendData]);

  const filteredAgencies = useMemo(() => {
    const term = agencySearch.trim().toLowerCase();
    if (!term) return agencies;
    return agencies.filter((agency) => (agency.name || '').toLowerCase().includes(term));
  }, [agencies, agencySearch]);

  const usersTableEntries = useMemo(() => {
    return (users || []).map(user => {
      // Determine user role: admin (superadmin), member (has agencies with non-client roles), or client (only client role memberships)
      let userRole = 'member';
      if (user.isSuperadmin) {
        userRole = 'admin';
      } else if (user.agencies && user.agencies.length > 0) {
        // Check if all memberships are client role
        const hasOnlyClientRole = user.agencies.every(a => a.role === 'client');
        if (hasOnlyClientRole) {
          userRole = 'client';
        } else {
          // Has at least one non-client role membership
          userRole = 'member';
        }
      } else if (user.totalAgencies === 0 && user.totalOwnedAgencies === 0) {
        // No agencies at all
        userRole = 'none';
      }

      return {
        ...user,
        userRole, // Add role for filtering
        createdAtFormatted: formatDateTime(user.createdAt),
        createdAtSort: user.createdAt ? new Date(user.createdAt).getTime() : 0, // For proper date sorting
        lastLoginFormatted: user.lastLogin ? formatDateTime(user.lastLogin) : 'Never',
        lastLoginSort: user.lastLogin ? new Date(user.lastLogin).getTime() : 0, // For proper date sorting
        statusBadge: user.isEmailVerified ? 'Verified' : 'Unverified',
        statusSort: user.isEmailVerified ? 1 : 0, // For proper boolean sorting
        planLabel: user.highestPlanTier ? PLAN_LABELS[user.highestPlanTier] || user.highestPlanTier : '—',
        planSort: user.highestPlanTier === 'enterprise' ? 4 : user.highestPlanTier === 'professional' ? 3 : user.highestPlanTier === 'starter' ? 2 : user.highestPlanTier === 'trial' ? 1 : 0, // For proper plan tier sorting
        agenciesSummary: user.totalAgencies > 0 
          ? `${user.totalAgencies} member${user.totalAgencies !== 1 ? 's' : ''}${user.totalOwnedAgencies > 0 ? `, ${user.totalOwnedAgencies} owned` : ''}`
          : 'No agencies',
        agenciesSort: user.totalAgencies || 0, // For proper numeric sorting
        agenciesList: user.agencies.length > 0
          ? user.agencies.slice(0, 2).map(a => a.name).join(', ') + (user.agencies.length > 2 ? ` +${user.agencies.length - 2}` : '')
          : '—',
        trialSort: user.hasTrialAgency ? 1 : 0 // For proper boolean sorting
      };
    });
  }, [users]);

  // Filter users based on active filters
  const filteredUsersTableEntries = useMemo(() => {
    let filtered = usersTableEntries;

    // Filter by trial
    if (userFilters.trial !== 'all') {
      filtered = filtered.filter(user => {
        if (userFilters.trial === 'yes') return user.hasTrialAgency === true;
        if (userFilters.trial === 'no') return user.hasTrialAgency === false;
        return true;
      });
    }

    // Filter by plan
    if (userFilters.plan !== 'all') {
      filtered = filtered.filter(user => user.highestPlanTier === userFilters.plan);
    }

    // Filter by status
    if (userFilters.status !== 'all') {
      filtered = filtered.filter(user => {
        if (userFilters.status === 'verified') return user.isEmailVerified === true;
        if (userFilters.status === 'unverified') return user.isEmailVerified === false;
        return true;
      });
    }

    // Filter by role
    if (userFilters.role !== 'all') {
      filtered = filtered.filter(user => user.userRole === userFilters.role);
    }

    return filtered;
  }, [usersTableEntries, userFilters]);

  const usersTableColumns = useMemo(() => ([
    { accessor: 'name', label: 'Name', sortable: true },
    { accessor: 'email', label: 'Email', sortable: true },
    { accessor: 'statusBadge', label: 'Status', sortable: true, sortKey: 'statusSort' },
    { accessor: 'userRole', label: 'Role', sortable: true },
    { accessor: 'agenciesSummary', label: 'Agencies', sortable: true, sortKey: 'agenciesSort' },
    { accessor: 'planLabel', label: 'Plan', sortable: true, sortKey: 'planSort' },
    { accessor: 'hasTrialAgency', label: 'Trial', sortable: true, sortKey: 'trialSort' },
    { accessor: 'createdAtFormatted', label: 'Created', sortable: true, sortKey: 'createdAtSort' },
    { accessor: 'lastLoginFormatted', label: 'Last Login', sortable: true, sortKey: 'lastLoginSort' },
  ]), []);

  const usersSummaryCards = useMemo(() => {
    if (!users.length) return [];

    const totalUsers = users.length;
    const verifiedUsers = users.filter(u => u.isEmailVerified).length;
    const usersOwningAgencies = users.filter(u => u.totalOwnedAgencies > 0).length;
    const usersMemberOfAgencies = users.filter(u => u.totalAgencies > 0).length;
    const superadmins = users.filter(u => u.isSuperadmin).length;
    const verificationRate = totalUsers > 0 ? Math.round((verifiedUsers / totalUsers) * 100) : 0;

    return [
      {
        key: 'total-users',
        title: 'Total Users',
        value: formatNumber(totalUsers),
        change: `${formatNumber(superadmins)} superadmins`,
        changeLabel: 'Platform administrators',
        icon: UserGroupIcon,
        color: 'blue',
        changeType: superadmins > 0 ? 'positive' : 'neutral',
      },
      {
        key: 'verified-users',
        title: 'Verified Users',
        value: `${formatNumber(verifiedUsers)} / ${formatNumber(totalUsers)}`,
        change: `${verificationRate}% verified`,
        changeLabel: 'Email verification rate',
        icon: ShieldCheckIcon,
        color: 'green',
        changeType: verificationRate >= 70 ? 'positive' : verificationRate <= 40 ? 'negative' : 'neutral',
      },
      {
        key: 'users-owning-agencies',
        title: 'Users Owning Agencies',
        value: formatNumber(usersOwningAgencies),
        change: `${formatNumber(usersMemberOfAgencies)} members`,
        changeLabel: 'Agency owners vs members',
        icon: BuildingOffice2Icon,
        color: 'purple',
        changeType: usersOwningAgencies > 0 ? 'positive' : 'neutral',
      },
      {
        key: 'total-clients',
        title: 'Total Clients',
        value: formatNumber(totalClients),
        change: 'Across all agencies',
        changeLabel: 'Client database size',
        icon: UserGroupIcon,
        color: 'indigo',
        changeType: totalClients > 0 ? 'positive' : 'neutral',
      },
    ];
  }, [users, totalClients]);

  const trialInterestTableEntries = useMemo(() => {
    return (trialInterestEntries || []).map(entry => ({
      ...entry,
      createdAtFormatted: formatDateTime(entry.createdAt),
      convertedAtFormatted: formatDateTime(entry.convertedAt)
    }));
  }, [trialInterestEntries]);

  const trialInterestTableColumns = useMemo(() => ([
    { accessor: 'email', label: 'Email' },
    { accessor: 'source', label: 'Source' },
    { accessor: 'createdAtFormatted', label: 'Captured' },
    { accessor: 'converted', label: 'Status' },
    { accessor: 'convertedAtFormatted', label: 'Converted At' },
    { accessor: 'referrer', label: 'Referrer' },
  ]), []);

  const overviewMetricCards = useMemo(() => {
    if (!overview) return [];

    const totalAgencies = overview.agencies?.total ?? 0;
    const enterpriseAgencies = overview.agencies?.byPlan?.enterprise ?? 0;
    const totalUsers = overview.users?.total ?? 0;
    const verifiedUsers = overview.users?.verified ?? 0;
    const verificationRate = totalUsers > 0 ? Math.round((verifiedUsers / totalUsers) * 100) : 0;
    const trialTotal = overview.trialInterest?.total ?? 0;
    const trialRecent = overview.trialInterest?.recent ?? 0;
    const convertedTrials = overview.trialInterest?.converted ?? 0;
    const conversionRate = overview.trialInterest?.conversionRate ?? 0;

    return [
      {
        key: 'agencies',
        title: 'Active Agencies',
        value: formatNumber(totalAgencies),
        change: `${formatNumber(enterpriseAgencies)} enterprise`,
        changeLabel: 'Top tier tenants',
        icon: BuildingOffice2Icon,
        color: 'blue',
        changeType: enterpriseAgencies > 0 ? 'positive' : 'neutral',
      },
      {
        key: 'users',
        title: 'Verified Users',
        value: `${formatNumber(verifiedUsers)} / ${formatNumber(totalUsers)}`,
        change: `${verificationRate}% verification`,
        changeLabel: 'Healthy onboarding rate',
        icon: UserGroupIcon,
        color: 'green',
        changeType: verificationRate >= 70 ? 'positive' : verificationRate <= 40 ? 'negative' : 'neutral',
      },
      {
        key: 'trials',
        title: 'Trial Signups (30d)',
        value: formatNumber(trialRecent),
        change: `${formatNumber(trialTotal)} all time`,
        changeLabel: 'Captured interest',
        icon: SparklesIcon,
        color: 'purple',
        changeType: trialRecent > 0 ? 'positive' : 'neutral',
      },
      {
        key: 'conversion',
        title: 'Conversion Rate',
        value: `${conversionRate}%`,
        change: `${formatNumber(convertedTrials)} converted`,
        changeLabel: 'Demand converting',
        icon: ArrowTrendingUpIcon,
        color: 'amber',
        changeType: conversionRate >= 20 ? 'positive' : conversionRate <= 5 ? 'negative' : 'neutral',
      },
      {
        key: 'superadmins',
        title: 'Superadmin Seats',
        value: formatNumber(overview.users?.superadmins ?? 0),
        change: 'Full access accounts',
        changeLabel: 'Platform guardians',
        icon: ShieldCheckIcon,
        color: 'indigo',
        changeType: (overview.users?.superadmins ?? 0) > 0 ? 'positive' : 'neutral',
      },
    ];
  }, [overview]);

  const trialInterestSummaryCards = useMemo(() => {
    if (!overview?.trialInterest) return [];

    const total = overview.trialInterest.total ?? 0;
    const converted = overview.trialInterest.converted ?? 0;
    const unconverted = Math.max(total - converted, 0);
    const recent = overview.trialInterest.recent ?? 0;
    const conversionRate = overview.trialInterest.conversionRate ?? 0;

    return [
      {
        key: 'trial-total',
        title: 'Total Signups',
        value: formatNumber(total),
        change: `${formatNumber(recent)} new this month`,
        changeLabel: 'Rolling 30 days',
        icon: SparklesIcon,
        color: 'purple',
        changeType: recent > 0 ? 'positive' : 'neutral',
      },
      {
        key: 'trial-converted',
        title: 'Converted Trials',
        value: formatNumber(converted),
        change: `${conversionRate}% conversion`,
        changeLabel: 'Across all captured interest',
        icon: ArrowTrendingUpIcon,
        color: 'green',
        changeType: conversionRate >= 20 ? 'positive' : conversionRate <= 5 ? 'negative' : 'neutral',
      },
      {
        key: 'trial-pipeline',
        title: 'In Pipeline',
        value: formatNumber(unconverted),
        change: 'Needs follow-up',
        changeLabel: 'Unconverted interest',
        icon: ClockIcon,
        color: 'orange',
        changeType: unconverted > 0 ? 'neutral' : 'positive',
      },
    ];
  }, [overview]);

  const globalFeatureDirty = useMemo(() => {
    for (const [key, baseline] of featureBaselineMap.entries()) {
      const draft = globalFeatureDraft[key] ?? baseline;
      if (draft !== baseline) {
        return true;
      }
    }
    return false;
  }, [featureBaselineMap, globalFeatureDraft]);

  const computeGlobalChanges = useCallback(() => {
    const changes = [];
    featureBaselineMap.forEach((baseline, key) => {
      const draft = globalFeatureDraft[key] ?? baseline;
      if (draft !== baseline) {
        changes.push({ key, from: baseline, to: draft });
      }
    });
    return changes;
  }, [featureBaselineMap, globalFeatureDraft]);

  const resetGlobalDraft = () => {
    const draft = {};
    featureBaselineMap.forEach((baseline, key) => {
      draft[key] = baseline;
    });
    setGlobalFeatureDraft(draft);
  };

  const openConfirmSave = () => {
    const changes = computeGlobalChanges();
    if (!changes.length) {
      toast('No changes to save.');
      return;
    }
    setPendingGlobalChanges(changes);
    setConfirmGlobalOpen(true);
  };

  const handleConfirmGlobalSave = async () => {
    const changes = computeGlobalChanges();
    if (!changes.length) {
      setConfirmGlobalOpen(false);
      return;
    }
    setGlobalSaving(true);
    try {
      for (const change of changes) {
        await apiCall(`/api/crm/admin/features/${change.key}`, {
          method: 'PATCH',
          data: { status: change.to }
        });
      }
      await refreshGlobalRegistry();
      await loadData();
      setPendingGlobalChanges([]);
      setConfirmGlobalOpen(false);
      toast.success('Global feature settings updated.');
    } catch (err) {
      console.error('Failed to save global feature changes:', err);
      toast.error(err?.response?.data?.message || 'Failed to save changes');
    } finally {
      setGlobalSaving(false);
    }
  };

  const toggleAgencyOpen = (agencyId) => {
    setOpenAgencies((prev) => ({
      ...prev,
      [agencyId]: !(prev?.[agencyId] ?? false),
    }));
  };

  const downloadUsersCSV = () => {
    try {
      // Use filtered data instead of all users
      const dataToExport = filteredUsersTableEntries;
      
      const headers = ['Name', 'Email', 'Status', 'Role', 'Superadmin', 'Total Agencies', 'Owned Agencies', 'Highest Plan', 'On Trial', 'Created', 'Last Login', 'Agencies List'];
      const rows = dataToExport.map(user => [
        user.name || '',
        user.email || '',
        user.statusBadge || '',
        user.userRole === 'admin' ? 'Admin' : user.userRole === 'client' ? 'Client' : 'Member',
        user.isSuperadmin ? 'Yes' : 'No',
        user.totalAgencies || 0,
        user.totalOwnedAgencies || 0,
        user.planLabel || '—',
        user.hasTrialAgency ? 'Yes' : 'No',
        user.createdAtFormatted || '',
        user.lastLoginFormatted || '',
        user.agencies.map(a => a.name).join('; ') || '—'
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          // Escape commas and quotes in CSV
          const cellStr = String(cell || '');
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Users CSV downloaded successfully');
    } catch (error) {
      console.error('Failed to download CSV:', error);
      toast.error('Failed to download CSV');
    }
  };

  const UsersRows = () => {
    const { data } = useTable();

    if (!data.length) {
      return (
        <TableRow>
          <TableCell colSpan={9} className="py-6 text-center text-muted-foreground">
            No users found.
          </TableCell>
        </TableRow>
      );
    }

    return (
      <>
        {data.map((user) => (
          <TableRow key={user.id}>
            <TableCell className="font-medium text-foreground">
              <div className="flex items-center gap-2">
                {user.name || '—'}
                {user.isSuperadmin && (
                  <Pill variant="info" size="sm" className="uppercase tracking-wide text-[10px]">
                    Admin
                  </Pill>
                )}
              </div>
            </TableCell>
            <TableCell className="text-muted-foreground">{user.email}</TableCell>
            <TableCell>
              <Pill
                variant={user.isEmailVerified ? 'success' : 'warning'}
                size="sm"
                className="uppercase tracking-wide text-[10px]"
              >
                {user.statusBadge}
              </Pill>
            </TableCell>
            <TableCell>
              <Pill
                variant={user.userRole === 'admin' ? 'info' : user.userRole === 'client' ? 'secondary' : 'default'}
                size="sm"
                className="uppercase tracking-wide text-[10px]"
              >
                {user.userRole === 'admin' ? 'Admin' : user.userRole === 'client' ? 'Client' : user.userRole === 'member' ? 'Member' : 'None'}
              </Pill>
            </TableCell>
            <TableCell>
              <div className="space-y-1">
                <div className="text-sm font-medium text-foreground">
                  {user.agenciesSummary}
                </div>
                {user.agencies.length > 0 && (
                  <div className="text-xs text-muted-foreground max-w-[200px] truncate">
                    {user.agenciesList}
                  </div>
                )}
              </div>
            </TableCell>
            <TableCell>
              {user.highestPlanTier ? (
                <Pill
                  variant={
                    user.highestPlanTier === 'enterprise' ? 'info' :
                    user.highestPlanTier === 'professional' ? 'success' :
                    user.highestPlanTier === 'starter' ? 'default' : 'warning'
                  }
                  size="sm"
                  className="uppercase tracking-wide text-[10px]"
                >
                  {user.planLabel}
                </Pill>
              ) : (
                <span className="text-muted-foreground text-sm">—</span>
              )}
            </TableCell>
            <TableCell>
              {user.hasTrialAgency ? (
                <Pill variant="warning" size="sm" className="uppercase tracking-wide text-[10px]">
                  Yes
                </Pill>
              ) : (
                <span className="text-muted-foreground text-sm">No</span>
              )}
            </TableCell>
            <TableCell className="text-muted-foreground text-sm">
              {user.createdAtFormatted}
            </TableCell>
            <TableCell className="text-muted-foreground text-sm">
              {user.lastLoginFormatted}
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  const TrialInterestRows = () => {
    const { data } = useTable();

    if (!data.length) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="py-6 text-center text-muted-foreground">
            No trial interest signups yet.
          </TableCell>
        </TableRow>
      );
    }

    return (
      <>
        {data.map((entry) => (
          <TableRow key={entry.id}>
            <TableCell className="font-medium text-foreground">{entry.email}</TableCell>
            <TableCell className="capitalize text-muted-foreground">{entry.source || '—'}</TableCell>
            <TableCell className="text-muted-foreground">{entry.createdAtFormatted}</TableCell>
            <TableCell>
              <Pill variant={entry.converted ? 'success' : 'info'} size="sm" className="uppercase tracking-wide text-[10px]">
                {entry.converted ? 'Converted' : 'Pending'}
              </Pill>
            </TableCell>
            <TableCell className="text-muted-foreground">{entry.convertedAtFormatted}</TableCell>
            <TableCell className="max-w-[240px] truncate text-muted-foreground">
              {entry.referrer || '—'}
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  const FeatureTableRows = () => {
    const { data } = useTable();

    if (!data.length) {
      return (
        <TableRow>
          <TableCell colSpan={5} className="py-6 text-center text-muted-foreground">
            No features match the current filters.
          </TableCell>
        </TableRow>
      );
    }

    return (
      <>
        {data.map((row) => (
          <TableRow key={row.key}>
            <TableCell>
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-foreground">{row.key}</span>
                  {row.dirty && (
                    <Pill variant="warning" size="sm" className="uppercase tracking-wide text-[10px]">
                      Unsaved
                    </Pill>
                  )}
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {row.description || <span className="italic">No description</span>}
                </p>
              </div>
            </TableCell>
            <TableCell>
              <Pill
                variant={FEATURE_STATUS_VARIANT[row.defaultStatus] || 'default'}
                size="sm"
                className="uppercase tracking-wide text-[10px]"
              >
                {row.defaultStatus.toUpperCase()}
              </Pill>
            </TableCell>
            <TableCell>
              <select
                className="rounded-md border border-input bg-background px-2 py-1 text-xs shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                value={row.draftStatus}
                onChange={(event) =>
                  setGlobalFeatureDraft((prev) => ({
                    ...prev,
                    [row.key]: event.target.value,
                  }))
                }
              >
                {FEATURE_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </TableCell>
            <TableCell>
              <div className="flex flex-wrap items-center gap-2">
                {row.hasPlanData ? (
                  PLAN_ORDER.map((planId) => {
                    if (typeof row.plans?.[planId] !== 'boolean') return null;
                    const enabled = row.plans[planId];
                    return (
                      <Pill
                        key={`${row.key}-${planId}`}
                        variant={enabled ? 'success' : 'default'}
                        size="sm"
                        className="uppercase tracking-wide text-[10px]"
                      >
                        {PLAN_LABELS[planId]}
                      </Pill>
                    );
                  })
                ) : (
                  <Pill variant="info" size="sm" className="uppercase tracking-wide text-[10px]">
                    No plan defaults
                  </Pill>
                )}
              </div>
            </TableCell>
            <TableCell className="text-right">
              <Button
                variant="ghost"
                size="icon"
                onClick={() =>
                  setFeatureInfoDialog({
                    open: true,
                    featureKey: row.key,
                    description: row.description || 'No description provided.',
                    defaultStatus: row.defaultStatus,
                    plans: row.plans,
                    hasPlanData: row.hasPlanData,
                  })
                }
              >
                <EyeIcon className="h-4 w-4" />
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  if (!isSuperadmin) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md text-center">
          <CardHeader>
            <CardTitle>Restricted Area</CardTitle>
            <CardDescription>
              You need superadmin privileges to access the control center.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Administrative Control Center</h1>
          <p className="text-sm text-muted-foreground">
            Monitor tenant health, adjust plan tiers, and manage feature availability with full audit trails.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full max-w-5xl grid-cols-8">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="features">Feature Flags</TabsTrigger>
          <TabsTrigger value="agencies">Agencies</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="trial-interest">Trial Interest</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="system">System</TabsTrigger>
          <TabsTrigger value="announcements">Announcements</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="space-y-4">
            {isLoading && (
              <Card>
                <CardHeader>
                  <CardTitle>Loading admin data…</CardTitle>
                  <CardDescription>Please wait while we fetch the latest overview.</CardDescription>
                </CardHeader>
              </Card>
            )}

            {error && (
              <Card className="border border-destructive/40 bg-destructive/5">
                <CardHeader>
                  <CardTitle className="text-destructive">Failed to Load</CardTitle>
                  <CardDescription>{error}</CardDescription>
                </CardHeader>
              </Card>
            )}

            {overview && (
              <>
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  {overviewMetricCards.map((metric) => (
                    <SmartDashboardCard
                      key={metric.key}
                      title={metric.title}
                      value={metric.value}
                      change={metric.change}
                      changeLabel={metric.changeLabel}
                      changeType={metric.changeType}
                      icon={metric.icon}
                      color={metric.color}
                    />
                  ))}
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <EnhancedBarChart
                    data={planMixChartData}
                    title="Plan Mix"
                    subtitle="Distribution by subscription tier"
                    xAxisKey="plan"
                    yAxisKey="count"
                    helpText="Monitor which plans agencies favor and spot upsell potential early."
                    className="shadow-sm border border-border"
                  />
                  <EnhancedLineChart
                    data={trialInterestLineData}
                    title="Trial Interest Trend"
                    subtitle="Daily signups across the last 30 days"
                    xAxisKey="date"
                    yAxisKey="value"
                    helpText="Track marketing momentum and identify when campaigns move the needle."
                    className="shadow-sm border border-border"
                  />
                </div>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="features">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Global Feature Flags</CardTitle>
                <CardDescription>
                  Platform-wide launch status. Features marked as hidden are removed from all navigation.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <form className="flex flex-wrap items-end gap-3" onSubmit={handleAddGlobalFeature}>
                  <div className="flex flex-col">
                    <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                      Feature Key
                    </label>
                    <input
                      type="text"
                      list="feature-key-options"
                      placeholder="Select or enter a feature key"
                      className="rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      value={newFeatureKey}
                      onChange={(event) => setNewFeatureKey(event.target.value)}
                      required
                    />
                    <datalist id="feature-key-options">
                      {featureKeyOptions.map((key) => (
                        <option key={key} value={key} />
                      ))}
                    </datalist>
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                      Status
                    </label>
                    <select
                      className="rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      value={newFeatureStatus}
                      onChange={(event) => setNewFeatureStatus(event.target.value)}
                    >
                      {FEATURE_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Button type="submit">
                    Add / Update Feature
                  </Button>
                </form>

                <div className="flex flex-wrap items-center gap-2">
                  {featureFilterOptions.map((option) => {
                    const isActive = featureStatusFilter === option.value;
                    return (
                      <Pill
                        key={option.value}
                        variant={isActive ? 'primary' : 'default'}
                        size="md"
                        className="cursor-pointer select-none px-3 py-1.5 text-xs font-semibold uppercase tracking-wide"
                        role="button"
                        tabIndex={0}
                        onClick={() => setFeatureStatusFilter(option.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setFeatureStatusFilter(option.value);
                          }
                        }}
                      >
                        {option.label}
                      </Pill>
                    );
                  })}
                </div>

                <div className="rounded-lg border border-border bg-card shadow-sm">
                  <div className="overflow-x-auto">
                    <TableProvider data={featureTableRows} columns={featureTableColumns}>
                      <TableSearch placeholder="Search features..." />
                      <Table className="min-w-full">
                        <TableHead>
                          <TableRow>
                            {featureTableColumns.map((column) => (
                              <TableHeaderCell
                                key={column.accessor}
                                column={column}
                                sortable={false}
                                className={column.accessor === 'actions' ? 'text-right' : undefined}
                              />
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <FeatureTableRows />
                        </TableBody>
                      </Table>
                      <TableFooter />
                    </TableProvider>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button onClick={openConfirmSave} disabled={!globalFeatureDirty || globalSaving}>
                    Save Changes
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={resetGlobalDraft}
                    disabled={!globalFeatureDirty || globalSaving}
                  >
                    Reset
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="agencies">
          <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-1">
                <label htmlFor="agency-search" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Search Agencies
                </label>
                <input
                  id="agency-search"
                  type="search"
                  placeholder="Start typing an agency name..."
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  value={agencySearch}
                  onChange={(event) => setAgencySearch(event.target.value)}
                />
              </div>
              <div className="text-xs text-muted-foreground whitespace-nowrap">
                Showing {filteredAgencies.length} / {agencies.length} agencies
              </div>
            </div>

            {filteredAgencies.map((agency) => {
              const featureKeys = booleanFeatureEntries(agency.planDefaults, agency.featureFlags);
              const overrides = agency.featureFlagOverrides || {};
              const draft = limitDrafts[agency.id] || {};
              const seatLimitValue = agency.seatLimit ?? agency.planDefaults?.maxSeats ?? null;
              const isOpen = openAgencies[agency.id] ?? false;

              return (
                <Card key={agency.id}>
                  <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                    {agency.name || 'Unnamed agency'}
                    <StatusPill status={agency.status} size="sm" className="uppercase tracking-wide text-[10px]" />
                      </CardTitle>
                      <CardDescription>
                        Created {agency.createdAt ? new Date(agency.createdAt).toLocaleDateString() : '—'}
                      </CardDescription>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleAgencyOpen(agency.id)}
                        className="h-8 w-8"
                        aria-label={isOpen ? 'Collapse agency' : 'Expand agency'}
                      >
                        <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </Button>
                      <label className="text-sm font-medium text-muted-foreground">
                        Plan
                      </label>
                      <select
                        className="rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        value={agency.planTier}
                        onChange={(event) => handlePlanChange(agency, event.target.value)}
                        disabled={busy[agency.id]}
                      >
                        {PLAN_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResetOverrides(agency)}
                        disabled={busy[agency.id] || !Object.keys(overrides).length}
                      >
                        Reset Overrides
                      </Button>
                    </div>
                  </CardHeader>
                  {isOpen && (
                    <CardContent className="space-y-4">
                      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        <div className="rounded-lg border border-muted/50 bg-muted/10 p-4">
                          <p className="text-xs uppercase tracking-wide text-muted-foreground">Seat Usage</p>
                          <p className="text-lg font-semibold">
                            {formatNumber(agency.seatUsage?.staff)} staff
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Limit: {seatLimitValue === null ? '—' : seatLimitValue === -1 ? 'Unlimited' : formatNumber(seatLimitValue)}
                          </p>
                        </div>
                        <div className="rounded-lg border border-muted/50 bg-muted/10 p-4">
                          <p className="text-xs uppercase tracking-wide text-muted-foreground">Client Users</p>
                          <p className="text-lg font-semibold">
                            {formatNumber(agency.seatUsage?.clients)}
                          </p>
                        </div>
                        <div className="rounded-lg border border-muted/50 bg-muted/10 p-4">
                          <p className="text-xs uppercase tracking-wide text-muted-foreground">Overrides</p>
                          <p className="text-lg font-semibold">
                            {Object.keys(overrides).length || 0}
                          </p>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                          Usage Limits
                        </h3>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                          <label className="flex flex-col text-sm">
                            <span className="text-muted-foreground text-xs uppercase tracking-wide mb-1">Seats</span>
                            <input
                              type="number"
                              placeholder="Default"
                              className="rounded-md border border-input bg-background px-2 py-1 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                              value={draft.seatLimit ?? ''}
                              onChange={(event) => updateLimitDraft(agency.id, 'seatLimit', event.target.value)}
                            />
                            <span className="text-xs text-muted-foreground mt-1">
                              Default: {agency.planDefaults?.maxSeats ?? '—'}
                            </span>
                          </label>
                          <label className="flex flex-col text-sm">
                            <span className="text-muted-foreground text-xs uppercase tracking-wide mb-1">Agencies</span>
                            <input
                              type="number"
                              placeholder="Default"
                              className="rounded-md border border-input bg-background px-2 py-1 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                              value={draft.maxAgencies ?? ''}
                              onChange={(event) => updateLimitDraft(agency.id, 'maxAgencies', event.target.value)}
                            />
                            <span className="text-xs text-muted-foreground mt-1">
                              Default: {agency.planDefaults?.maxAgencies ?? '—'}
                            </span>
                          </label>
                          <label className="flex flex-col text-sm">
                            <span className="text-muted-foreground text-xs uppercase tracking-wide mb-1">Clients</span>
                            <input
                              type="number"
                              placeholder="Default"
                              className="rounded-md border border-input bg-background px-2 py-1 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                              value={draft.maxClients ?? ''}
                              onChange={(event) => updateLimitDraft(agency.id, 'maxClients', event.target.value)}
                            />
                            <span className="text-xs text-muted-foreground mt-1">
                              Default: {agency.planDefaults?.maxClients ?? '—'}
                            </span>
                          </label>
                          <label className="flex flex-col text-sm">
                            <span className="text-muted-foreground text-xs uppercase tracking-wide mb-1">Accounts</span>
                            <input
                              type="number"
                              placeholder="Default"
                              className="rounded-md border border-input bg-background px-2 py-1 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                              value={draft.maxAccounts ?? ''}
                              onChange={(event) => updateLimitDraft(agency.id, 'maxAccounts', event.target.value)}
                            />
                            <span className="text-xs text-muted-foreground mt-1">
                              Default: {agency.planDefaults?.maxAccounts ?? '—'}
                            </span>
                          </label>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            onClick={() => handleSaveLimits(agency)}
                            disabled={busy[`limits:${agency.id}`]}
                          >
                            Save Limits
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResetLimits(agency)}
                            disabled={busy[`limits:${agency.id}`]}
                          >
                            Reset to Defaults
                          </Button>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                          Feature Flags
                        </h3>
                        {featureKeys.length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            This plan does not expose toggleable features.
                          </p>
                        ) : (
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {featureKeys.map((featureKey) => {
                              const defaultValue = agency.planDefaults?.[featureKey];
                              const currentValue = agency.featureFlags?.[featureKey];
                              const isOverride = Object.prototype.hasOwnProperty.call(overrides, featureKey);
                              const busyKey = busy[`${agency.id}:${featureKey}`];
                              const globalStatus = featureRegistry?.[featureKey]?.status || 'live';

                              return (
                                <label
                                  key={featureKey}
                                  className="flex items-center justify-between rounded-md border border-muted/40 bg-background px-3 py-2 text-sm shadow-sm"
                                >
                                  <div className="flex flex-col">
                                    <span className="font-medium text-foreground">{featureKey}</span>
                                    <span className="text-xs text-muted-foreground">
                                      Default: {String(defaultValue)}{isOverride ? ' • overridden' : ''}
                                      {' • '}
                                      Global: {globalStatus}
                                    </span>
                                  </div>
                                  <Switch
                                    checked={!!currentValue}
                                    disabled={busyKey}
                                    onCheckedChange={(checked) => handleFeatureToggle(agency, featureKey, checked)}
                                  />
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}

            {!filteredAgencies.length && !isLoading && !error && (
              <Card>
                <CardHeader>
                  <CardTitle>No Agencies Found</CardTitle>
                  <CardDescription>
                    Agencies will appear here once tenants are provisioned.
                  </CardDescription>
                </CardHeader>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="space-y-4">
            {!!usersSummaryCards.length && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {usersSummaryCards.map((metric) => (
                  <SmartDashboardCard
                    key={metric.key}
                    title={metric.title}
                    value={metric.value}
                    change={metric.change}
                    changeLabel={metric.changeLabel}
                    changeType={metric.changeType}
                    icon={metric.icon}
                    color={metric.color}
                  />
                ))}
              </div>
            )}

            <div className="rounded-lg border border-border bg-card shadow-sm">
              <div className="flex items-center justify-between p-4 border-b border-border">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">All Users</h3>
                  <p className="text-sm text-muted-foreground">
                    Complete list of all users with their agencies, subscription plans, and trial status
                  </p>
                </div>
                <Button variant="outline" onClick={downloadUsersCSV} disabled={!filteredUsersTableEntries.length}>
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  Download CSV ({filteredUsersTableEntries.length})
                </Button>
              </div>
              
              {/* Filter Controls */}
              <div className="p-4 border-b border-border bg-muted/30">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Trial Filter */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">Trial</label>
                    <select
                      value={userFilters.trial}
                      onChange={(e) => setUserFilters(prev => ({ ...prev, trial: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                    >
                      <option value="all">All</option>
                      <option value="yes">On Trial</option>
                      <option value="no">Not on Trial</option>
                    </select>
                  </div>

                  {/* Plan Filter */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">Plan</label>
                    <select
                      value={userFilters.plan}
                      onChange={(e) => setUserFilters(prev => ({ ...prev, plan: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                    >
                      <option value="all">All Plans</option>
                      <option value="trial">Trial</option>
                      <option value="starter">Starter</option>
                      <option value="professional">Professional</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">Status</label>
                    <select
                      value={userFilters.status}
                      onChange={(e) => setUserFilters(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                    >
                      <option value="all">All Status</option>
                      <option value="verified">Verified</option>
                      <option value="unverified">Unverified</option>
                    </select>
                  </div>

                  {/* Role Filter */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">Role</label>
                    <select
                      value={userFilters.role}
                      onChange={(e) => setUserFilters(prev => ({ ...prev, role: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                    >
                      <option value="all">All Roles</option>
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                      <option value="client">Client</option>
                    </select>
                  </div>
                </div>
                
                {/* Clear Filters Button */}
                {(userFilters.trial !== 'all' || userFilters.plan !== 'all' || userFilters.status !== 'all' || userFilters.role !== 'all') && (
                  <div className="mt-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setUserFilters({ trial: 'all', plan: 'all', status: 'all', role: 'all' })}
                      className="text-xs"
                    >
                      Clear Filters
                    </Button>
                  </div>
                )}
              </div>

              <TableProvider data={filteredUsersTableEntries} columns={usersTableColumns}>
                <TableSearch placeholder="Search users by name or email..." />
                <div className="overflow-x-auto">
                  <Table className="min-w-full">
                    <TableHead>
                      <TableRow>
                        {usersTableColumns.map((column) => (
                          <TableHeaderCell key={column.accessor} column={column} />
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <UsersRows />
                    </TableBody>
                  </Table>
                </div>
                <TableFooter />
              </TableProvider>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="trial-interest">
          <div className="space-y-4">
            {!!trialInterestSummaryCards.length && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {trialInterestSummaryCards.map((metric) => (
                  <SmartDashboardCard
                    key={metric.key}
                    title={metric.title}
                    value={metric.value}
                    change={metric.change}
                    changeLabel={metric.changeLabel}
                    changeType={metric.changeType}
                    icon={metric.icon}
                    color={metric.color}
                  />
                ))}
              </div>
            )}

            {trialInterestLineData.length > 0 ? (
              <EnhancedLineChart
                data={trialInterestLineData}
                title="Interest Trend (30 Days)"
                subtitle="Track marketing-driven demand across the last month"
                xAxisKey="date"
                yAxisKey="value"
                helpText="Align nurture campaigns to peaks and troughs in trial interest volume."
                className="shadow-sm border border-border"
              />
            ) : (
              <Card className="border border-border bg-card shadow-sm">
                <CardHeader>
                  <CardTitle>Interest Trend (30 Days)</CardTitle>
                  <CardDescription>We need more captured interest to visualize the trend.</CardDescription>
                </CardHeader>
              </Card>
            )}

            <div className="rounded-lg border border-border bg-card shadow-sm">
              <TableProvider data={trialInterestTableEntries} columns={trialInterestTableColumns}>
                <TableSearch placeholder="Search captured interest..." />
                <div className="overflow-x-auto">
                  <Table className="min-w-full">
                    <TableHead>
                      <TableRow>
                        {trialInterestTableColumns.map((column) => (
                          <TableHeaderCell key={column.accessor} column={column} />
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TrialInterestRows />
                    </TableBody>
                  </Table>
                </div>
                <TableFooter />
              </TableProvider>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="templates">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Global Template Library</CardTitle>
                    <CardDescription>
                      Manage reusable workflow, form, and email templates for all agencies
                    </CardDescription>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      onClick={async () => {
                        try {
                          // Export all templates
                          const response = await adminSystemAPI.exportTemplates();
                          const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `templates-export-${new Date().toISOString().split('T')[0]}.json`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                          toast.success('Templates exported successfully');
                        } catch (err) {
                          console.error('Failed to export templates:', err);
                          toast.error('Failed to export templates');
                        }
                      }}
                    >
                      Export All
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = '.json';
                        input.onchange = async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          
                          try {
                            const text = await file.text();
                            const jsonData = JSON.parse(text);
                            
                            // Handle both single template and export format
                            const templates = jsonData.templates || (Array.isArray(jsonData) ? jsonData : [jsonData]);
                            
                            if (templates.length === 0) {
                              toast.error('No templates found in file');
                              return;
                            }
                            
                            // Store templates in closure for use in dialog callbacks
                            const templatesToImport = templates;
                            
                            setConfirmDialog({
                              open: true,
                              title: 'Import Templates',
                              message: `Import ${templatesToImport.length} template(s)? Existing templates with the same slug will be skipped unless you choose to overwrite.`,
                              onConfirm: () => {
                                setConfirmDialog(prev => ({
                                  ...prev,
                                  message: 'Overwrite existing templates?',
                                  onConfirm: async () => {
                                    try {
                                      const response = await adminSystemAPI.importTemplates(templatesToImport, true);
                                      if (response.data?.success) {
                                        const { created, updated, skipped, errors } = response.data.data;
                                        let message = `Import complete: ${created} created, ${updated} updated, ${skipped} skipped`;
                                        if (errors && errors.length > 0) {
                                          message += `, ${errors.length} errors`;
                                          console.error('Import errors:', errors);
                                        }
                                        toast.success(message);
                                        loadTemplates();
                                      } else {
                                        toast.error(response.data?.message || 'Failed to import templates');
                                      }
                                    } catch (err) {
                                      console.error('Failed to import templates:', err);
                                      toast.error('Failed to import templates. Please check the JSON format.');
                                    }
                                    setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, confirmLabel: 'Confirm', variant: 'default' });
                                  },
                                  onCancel: async () => {
                                    try {
                                      const response = await adminSystemAPI.importTemplates(templatesToImport, false);
                                      if (response.data?.success) {
                                        const { created, updated, skipped, errors } = response.data.data;
                                        let message = `Import complete: ${created} created, ${updated} updated, ${skipped} skipped`;
                                        if (errors && errors.length > 0) {
                                          message += `, ${errors.length} errors`;
                                          console.error('Import errors:', errors);
                                        }
                                        toast.success(message);
                                        loadTemplates();
                                      } else {
                                        toast.error(response.data?.message || 'Failed to import templates');
                                      }
                                    } catch (err) {
                                      console.error('Failed to import templates:', err);
                                      toast.error('Failed to import templates. Please check the JSON format.');
                                    }
                                    setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, confirmLabel: 'Confirm', variant: 'default' });
                                  }
                                }));
                              },
                              confirmLabel: 'Continue',
                              variant: 'default'
                            });
                          } catch (err) {
                            console.error('Failed to import templates:', err);
                            toast.error('Failed to import templates. Please check the JSON format.');
                          }
                        };
                        input.click();
                      }}
                    >
                      Import JSON
                    </Button>
                    <Button
                      variant="outline"
                      onClick={async () => {
                        try {
                          setConfirmDialog({
                            open: true,
                            title: 'Import Default Templates',
                            message: 'This will import the default templates (relocation workflows: NIE, TIE, Empadronamiento, Housing Search, School Enrollment; form templates: Lead Intake, Simple Survey) as global templates. Existing templates with the same slug will be skipped unless you choose to overwrite. Continue?',
                            onConfirm: async () => {
                              setConfirmDialog(prev => ({
                                ...prev,
                                open: false,
                                message: 'Overwrite existing templates? (Click Confirm to overwrite, Cancel to skip existing)',
                                onConfirm: async () => {
                                  try {
                                    const response = await adminSystemAPI.seedDefaultTemplates(true);
                                    if (response.data?.success) {
                                      const { created, updated, skipped } = response.data.data;
                                      toast.success(
                                        `Templates imported: ${created} created, ${updated} updated, ${skipped} skipped`
                                      );
                                      loadTemplates();
                                    } else {
                                      toast.error(response.data?.message || 'Failed to import templates');
                                    }
                                  } catch (err) {
                                    console.error('Failed to seed default templates:', err);
                                    toast.error(err?.response?.data?.message || 'Failed to import default templates');
                                  }
                                  setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, confirmLabel: 'Confirm', variant: 'default' });
                                },
                                onCancel: async () => {
                                  try {
                                    const response = await adminSystemAPI.seedDefaultTemplates(false);
                                    if (response.data?.success) {
                                      const { created, updated, skipped } = response.data.data;
                                      toast.success(
                                        `Templates imported: ${created} created, ${updated} updated, ${skipped} skipped`
                                      );
                                      loadTemplates();
                                    } else {
                                      toast.error(response.data?.message || 'Failed to import templates');
                                    }
                                  } catch (err) {
                                    console.error('Failed to seed default templates:', err);
                                    toast.error(err?.response?.data?.message || 'Failed to import default templates');
                                  }
                                  setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, confirmLabel: 'Confirm', variant: 'default' });
                                }
                              }));
                            },
                            confirmLabel: 'Continue',
                            variant: 'default'
                          });
                        } catch (err) {
                          console.error('Failed to open confirm dialog:', err);
                          toast.error('Failed to open confirmation dialog');
                        }
                      }}
                    >
                      Import Default Templates
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setTemplateForm({ id: null, type: 'task', slug: '', title: '', description: '', category: '', tags: '', estimatedDuration: '', stepsJson: '[]', contentJson: '{}', changeNotes: '' });
                        setTemplateFormOpen(true);
                      }}
                    >
                      Create (JSON)
                    </Button>
                    <Button onClick={() => setTemplateTypeSelectorOpen(true)}>
                      Create with Builder
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {templatesLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading templates...</div>
                ) : templateError ? (
                  <div className="text-center py-8 text-destructive">{templateError}</div>
                ) : libraryTemplates.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No templates yet. Create your first template to get started.</div>
                ) : (
                  <div className="space-y-3">
                    {libraryTemplates.map((template) => (
                      <Card key={template.id} className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-foreground">
                                {template.title || 'Untitled Template'}
                                {template.title === 'Untitled Form' && template.slug && (
                                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                                    ({template.slug.split('-').pop()})
                                  </span>
                                )}
                              </h3>
                              {template.isNew && (
                                <Pill variant="info" size="sm">{template.highlightLabel || 'New'}</Pill>
                              )}
                              {(() => {
                                // Check if template has unpublished changes
                                // Only show if the template form is open and this is the template being edited
                                const isCurrentlyEditing = templateFormOpen && templateForm.id === template.id;
                                let hasUnpublishedChanges = false;
                                
                                if (isCurrentlyEditing && templateOriginalState && template.status === 'published') {
                                  // Compare current form state with original state (excluding changeNotes)
                                  const currentState = JSON.stringify({
                                    title: templateForm.title,
                                    description: templateForm.description,
                                    category: templateForm.category,
                                    tags: templateForm.tags,
                                    estimatedDuration: templateForm.estimatedDuration,
                                    stepsJson: templateForm.stepsJson,
                                    contentJson: templateForm.contentJson
                                  });
                                  const originalState = JSON.stringify({
                                    title: templateOriginalState.title,
                                    description: templateOriginalState.description,
                                    category: templateOriginalState.category,
                                    tags: templateOriginalState.tags,
                                    estimatedDuration: templateOriginalState.estimatedDuration,
                                    stepsJson: templateOriginalState.stepsJson,
                                    contentJson: templateOriginalState.contentJson
                                  });
                                  hasUnpublishedChanges = currentState !== originalState;
                                }
                                
                                if (hasUnpublishedChanges) {
                                  return <Pill variant="warning" size="sm">Unpublished Changes</Pill>;
                                }
                                return <Pill variant="default" size="sm" className="capitalize">{template.status}</Pill>;
                              })()}
                            </div>
                            {template.description && (
                              <p className="text-sm text-muted-foreground mb-2">{template.description}</p>
                            )}
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <Pill variant="default" size="sm" className="capitalize">
                                {template.type === 'task' ? 'Task Template' : 
                                 template.type === 'workflow' ? 'Automation Workflow' : 
                                 template.type || 'Template'}
                              </Pill>
                              {template.category && <span>Category: {template.category}</span>}
                              {template.version && <span>Version: {template.version}</span>}
                              {template.slug && <span className="text-[10px] font-mono text-muted-foreground/70">Slug: {template.slug}</span>}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                try {
                                  const response = await adminSystemAPI.exportTemplate(template.id);
                                  const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `${template.slug || 'template'}-${Date.now()}.json`;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                  toast.success('Template exported successfully');
                                } catch (err) {
                                  console.error('Failed to export template:', err);
                                  toast.error('Failed to export template');
                                }
                              }}
                              title="Export as JSON"
                            >
                              Export
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                // Prepare form data based on template type
                                const content = template.content || {};
                                let stepsJson = '[]';
                                let contentJson = '{}';
                                
                                if (template.type === 'task') {
                                  // For task templates, extract steps and estimatedDuration
                                  stepsJson = JSON.stringify(content.steps || [], null, 2);
                                } else {
                                  // For workflow/form/email templates, use full content
                                  contentJson = JSON.stringify(content, null, 2);
                                }
                                
                                const formData = {
                                  id: template.id,
                                  type: template.type || 'task',
                                  slug: template.slug || '',
                                  title: template.title || '',
                                  description: template.description || '',
                                  category: template.category || '',
                                  tags: (template.tags || []).join(', '),
                                  estimatedDuration: content.estimatedDuration || '',
                                  stepsJson: stepsJson,
                                  contentJson: contentJson,
                                  changeNotes: ''
                                };
                                
                                setTemplateForm(formData);
                                // Store original state for change detection
                                setTemplateOriginalState(JSON.parse(JSON.stringify(formData)));
                                setTemplateFormOpen(true);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={(() => {
                                // Disable if template is published and:
                                // 1. Not currently being edited, OR
                                // 2. Being edited but no changes made
                                if (template.status === 'published') {
                                  const isCurrentlyEditing = templateFormOpen && templateForm.id === template.id;
                                  if (!isCurrentlyEditing) {
                                    return true; // Not editing, so no changes to publish
                                  }
                                  if (!templateOriginalState) {
                                    return true; // No original state to compare
                                  }
                                  // Compare current form state with original state (excluding changeNotes)
                                  const currentState = JSON.stringify({
                                    title: templateForm.title,
                                    description: templateForm.description,
                                    category: templateForm.category,
                                    tags: templateForm.tags,
                                    estimatedDuration: templateForm.estimatedDuration,
                                    stepsJson: templateForm.stepsJson,
                                    contentJson: templateForm.contentJson
                                  });
                                  const originalState = JSON.stringify({
                                    title: templateOriginalState.title,
                                    description: templateOriginalState.description,
                                    category: templateOriginalState.category,
                                    tags: templateOriginalState.tags,
                                    estimatedDuration: templateOriginalState.estimatedDuration,
                                    stepsJson: templateOriginalState.stepsJson,
                                    contentJson: templateOriginalState.contentJson
                                  });
                                  if (currentState === originalState) {
                                    return true; // No changes made
                                  }
                                }
                                return false;
                              })()}
                              onClick={() => {
                                setPublishState({
                                  open: true,
                                  templateId: template.id,
                                  highlightDays: 7,
                                  highlightLabel: 'New',
                                  notifyUsers: true,
                                  severity: 'info',
                                  announcementTitle: `New template: ${template.title}`,
                                  announcementMessage: `A new template "${template.title}" is now available.`,
                                  expiresInDays: 7,
                                  changeNotes: ''
                                });
                              }}
                              title={(() => {
                                if (template.status === 'published') {
                                  const isCurrentlyEditing = templateFormOpen && templateForm.id === template.id;
                                  if (!isCurrentlyEditing) {
                                    return 'No changes to publish';
                                  }
                                  if (!templateOriginalState) {
                                    return 'No changes to publish';
                                  }
                                  const currentState = JSON.stringify({
                                    title: templateForm.title,
                                    description: templateForm.description,
                                    category: templateForm.category,
                                    tags: templateForm.tags,
                                    estimatedDuration: templateForm.estimatedDuration,
                                    stepsJson: templateForm.stepsJson,
                                    contentJson: templateForm.contentJson
                                  });
                                  const originalState = JSON.stringify({
                                    title: templateOriginalState.title,
                                    description: templateOriginalState.description,
                                    category: templateOriginalState.category,
                                    tags: templateOriginalState.tags,
                                    estimatedDuration: templateOriginalState.estimatedDuration,
                                    stepsJson: templateOriginalState.stepsJson,
                                    contentJson: templateOriginalState.contentJson
                                  });
                                  if (currentState === originalState) {
                                    return 'No changes to publish';
                                  }
                                }
                                return 'Publish template';
                              })()}
                            >
                              Publish
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setDeleteTemplateDialog({
                                  open: true,
                                  templateId: template.id,
                                  templateTitle: template.title
                                });
                              }}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="system">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>System Configuration</CardTitle>
                <CardDescription>
                  Manage global banner and maintenance mode settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Banner Settings */}
                <div className="space-y-4 p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-foreground">Global Banner</h3>
                      <p className="text-sm text-muted-foreground">Display a message banner at the top of all pages</p>
                    </div>
                    <Switch
                      checked={systemConfigDraft.banner?.enabled || false}
                      onCheckedChange={(checked) => {
                        setSystemConfigDraft(prev => ({
                          ...prev,
                          banner: { ...prev.banner, enabled: checked }
                        }));
                      }}
                    />
                  </div>
                  {systemConfigDraft.banner?.enabled && (
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-foreground mb-1 block">Message</label>
                        <Textarea
                          value={systemConfigDraft.banner?.message || ''}
                          onChange={(e) => {
                            setSystemConfigDraft(prev => ({
                              ...prev,
                              banner: { ...prev.banner, message: e.target.value }
                            }));
                          }}
                          placeholder="Enter banner message..."
                          rows={2}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-foreground mb-1 block">Severity</label>
                        <select
                          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={systemConfigDraft.banner?.severity || 'info'}
                          onChange={(e) => {
                            setSystemConfigDraft(prev => ({
                              ...prev,
                              banner: { ...prev.banner, severity: e.target.value }
                            }));
                          }}
                        >
                          <option value="info">Info</option>
                          <option value="warning">Warning</option>
                          <option value="error">Error</option>
                          <option value="success">Success</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {/* Maintenance Mode */}
                <div className="space-y-4 p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-foreground">Maintenance Mode</h3>
                      <p className="text-sm text-muted-foreground">Enable to show maintenance page to all users</p>
                    </div>
                    <Switch
                      checked={systemConfigDraft.maintenance?.enabled || false}
                      onCheckedChange={(checked) => {
                        setSystemConfigDraft(prev => ({
                          ...prev,
                          maintenance: { ...prev.maintenance, enabled: checked }
                        }));
                      }}
                    />
                  </div>
                  {systemConfigDraft.maintenance?.enabled && (
                    <div>
                      <label className="text-sm font-medium text-foreground mb-1 block">Maintenance Message</label>
                      <Textarea
                        value={systemConfigDraft.maintenance?.message || ''}
                        onChange={(e) => {
                          setSystemConfigDraft(prev => ({
                            ...prev,
                            maintenance: { ...prev.maintenance, message: e.target.value }
                          }));
                        }}
                        placeholder="Enter maintenance message..."
                        rows={3}
                      />
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={async () => {
                      setSystemConfigSaving(true);
                      try {
                        await adminSystemAPI.updateConfig({
                          banner: systemConfigDraft.banner,
                          maintenance: systemConfigDraft.maintenance
                        });
                        toast.success('System configuration updated');
                        loadSystemConfig();
                      } catch (err) {
                        toast.error('Failed to update system configuration');
                      } finally {
                        setSystemConfigSaving(false);
                      }
                    }}
                    disabled={systemConfigSaving}
                  >
                    {systemConfigSaving ? 'Saving...' : 'Save Configuration'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => loadSystemConfig()}
                    disabled={systemConfigLoading}
                  >
                    Reset
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="announcements">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>System Announcements</CardTitle>
                    <CardDescription>
                      Send notification announcements to all users
                    </CardDescription>
                  </div>
                  <Button onClick={() => {
                    setAnnouncementDraft({ title: '', message: '', severity: 'info', expiresInDays: 7 });
                    setAnnouncementFormOpen(true);
                  }}>
                    Create Announcement
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {announcementsLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading announcements...</div>
                ) : announcements.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No announcements yet.</div>
                ) : (
                  <div className="space-y-3">
                    {announcements.map((announcement) => (
                      <Card key={announcement.id} className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-foreground">{announcement.title}</h3>
                              <Pill variant={announcement.severity === 'error' ? 'error' : announcement.severity === 'warning' ? 'warning' : announcement.severity === 'success' ? 'success' : 'info'} size="sm" className="capitalize">
                                {announcement.severity}
                              </Pill>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">{announcement.message}</p>
                            <div className="text-xs text-muted-foreground">
                              Published: {formatDateTime(announcement.publishedAt)}
                              {announcement.expiresAt && ` • Expires: ${formatDateTime(announcement.expiresAt)}`}
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                setConfirmDialog({
                                  open: true,
                                  title: 'Delete Announcement',
                                  message: `Are you sure you want to delete "${announcement.title}"? This will suppress the announcement for all users.`,
                                  onConfirm: async () => {
                                    try {
                                      await adminSystemAPI.deleteAnnouncement(announcement.id);
                                      toast.success('Announcement deleted successfully');
                                      loadAnnouncements();
                                    } catch (err) {
                                      console.error('Failed to delete announcement:', err);
                                      toast.error(err?.response?.data?.message || 'Failed to delete announcement');
                                    }
                                    setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, confirmLabel: 'Confirm', variant: 'default' });
                                  },
                                  confirmLabel: 'Delete',
                                  variant: 'destructive'
                                });
                              }}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={confirmGlobalOpen} onOpenChange={(open) => { if (!open) handleCloseConfirm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply Global Feature Changes</DialogTitle>
            <DialogDescription>
              These updates affect availability across all tenants. Please confirm before proceeding.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {pendingGlobalChanges.length === 0 ? (
              <p className="text-sm text-muted-foreground">No changes detected.</p>
            ) : (
              <ul className="space-y-2">
                {pendingGlobalChanges.map((change) => {
                  const item = featureItems.find((entry) => entry.key === change.key);
                  const plans = item?.plans || {};
                  const hasPlanData = PLAN_ORDER.some((planId) => typeof plans?.[planId] === 'boolean');
                  return (
                    <li key={change.key} className="rounded-md border border-muted/40 bg-muted/10 px-3 py-2 text-sm space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-foreground">{change.key}</span>
                        <span className="text-xs text-muted-foreground">
                          {change.from} → {change.to}
                        </span>
                      </div>
                      {hasPlanData && (
                        <div className="flex flex-wrap gap-1 text-[11px] text-muted-foreground">
                          {PLAN_ORDER.map((planId) => {
                            if (typeof plans?.[planId] !== 'boolean') return null;
                            const enabled = plans[planId];
                            return (
                              <span
                                key={`${change.key}-${planId}`}
                                className={`rounded-full px-2 py-0.5 font-medium ${enabled ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200' : 'bg-muted text-muted-foreground'}`}
                              >
                                {PLAN_LABELS[planId]}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={handleCloseConfirm} disabled={globalSaving}>
              Cancel
            </Button>
            <Button onClick={handleConfirmGlobalSave} disabled={globalSaving || pendingGlobalChanges.length === 0}>
              {globalSaving ? 'Saving…' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={featureInfoDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setFeatureInfoDialog({
              open: false,
              featureKey: '',
              description: '',
              defaultStatus: '',
              plans: {},
              hasPlanData: false,
            });
          } else {
            setFeatureInfoDialog((prev) => ({
              ...prev,
              open: true,
            }));
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3">
              <span>{featureInfoDialog.featureKey}</span>
              {featureInfoDialog.defaultStatus && (
                <Pill
                  variant={FEATURE_STATUS_VARIANT[featureInfoDialog.defaultStatus] || 'default'}
                  size="sm"
                  className="uppercase tracking-wide text-[10px]"
                >
                  {featureInfoDialog.defaultStatus.toUpperCase()}
                </Pill>
              )}
            </DialogTitle>
            <DialogDescription>
              Global description and plan defaults for this feature.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm text-muted-foreground">
            <div>
              <p className="text-foreground">
                {featureInfoDialog.description || 'No description available.'}
              </p>
            </div>
            <div className="space-y-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Plan Coverage
              </span>
              {featureInfoDialog.hasPlanData ? (
                <div className="flex flex-wrap gap-2">
                  {PLAN_ORDER.map((planId) => {
                    if (typeof featureInfoDialog.plans?.[planId] !== 'boolean') return null;
                    const enabled = featureInfoDialog.plans[planId];
                    return (
                      <Pill
                        key={planId}
                        variant={enabled ? 'success' : 'default'}
                        size="sm"
                        className="uppercase tracking-wide text-[10px]"
                      >
                        {PLAN_LABELS[planId]}
                      </Pill>
                    );
                  })}
                </div>
              ) : (
                <Pill variant="info" size="sm" className="uppercase tracking-wide text-[10px]">
                  No plan defaults
                </Pill>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setFeatureInfoDialog({
                  open: false,
                  featureKey: '',
                  description: '',
                  defaultStatus: '',
                  plans: {},
                  hasPlanData: false,
                })
              }
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Form Dialog */}
      <Dialog open={templateFormOpen} onOpenChange={setTemplateFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{templateForm.id ? 'Edit Template' : 'Create Template'}</DialogTitle>
            <DialogDescription>
              {templateForm.id ? 'Update template details' : 'Create a new reusable template. See docs/TEMPLATE_BUILDER_GUIDE.md for JSON structure examples.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Template Type</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={templateForm.type}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, type: e.target.value }))}
                disabled={!!templateForm.id}
              >
                <option value="task">Task Template (Step-by-step processes)</option>
                <option value="workflow">Automation Workflow (Trigger-based)</option>
                <option value="form">Form</option>
                <option value="email">Email Template</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                {templateForm.type === 'task' && 'Step-by-step process templates (NIE, TIE, relocation workflows, etc.)'}
                {templateForm.type === 'workflow' && 'Automation workflows with triggers and actions (visual builder)'}
                {templateForm.type === 'form' && 'Dynamic forms with fields and validation'}
                {templateForm.type === 'email' && 'Email templates with HTML/text content'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Slug</label>
              <Input
                value={templateForm.slug}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
                placeholder="nie-application-process"
                disabled={!!templateForm.id}
              />
              <p className="text-xs text-muted-foreground mt-1">Lowercase, hyphens, no spaces (e.g., nie-application-process)</p>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Title</label>
              <Input
                value={templateForm.title}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Template title"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Description</label>
              <Textarea
                value={templateForm.description}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Template description"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Category</label>
                <Input
                  value={templateForm.category}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="e.g., immigration, onboarding"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Tags (comma-separated)</label>
                <Input
                  value={templateForm.tags}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="immigration, nie, spain"
                />
              </div>
            </div>
            {templateForm.type === 'task' && (
              <>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Estimated Duration</label>
                  <Input
                    value={templateForm.estimatedDuration}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, estimatedDuration: e.target.value }))}
                    placeholder="e.g., 2-4 weeks"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Steps (JSON)</label>
                  <Textarea
                    value={templateForm.stepsJson}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, stepsJson: e.target.value }))}
                    placeholder='[{"title": "Step 1", "description": "...", "order": 1, "estimatedDuration": "1 day", "requiredDocuments": [], "links": []}]'
                    rows={12}
                    className="font-mono text-xs"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    See docs/TEMPLATE_BUILDER_GUIDE.md for structure. Each step should have: title, description, order, estimatedDuration, requiredDocuments, links.
                  </p>
                </div>
              </>
            )}
            {templateForm.type === 'workflow' && (
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Workflow Content (JSON)</label>
                <Textarea
                  value={templateForm.contentJson}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, contentJson: e.target.value }))}
                  placeholder='{"trigger": {...}, "actions": [...], "settings": {...}, "visualData": {...}}'
                  rows={12}
                  className="font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Automation workflow with triggers and actions. Use the visual builder for easier creation.
                </p>
              </div>
            )}
            {templateForm.type === 'form' && (
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Form Content (JSON)</label>
                <Textarea
                  value={templateForm.contentJson}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, contentJson: e.target.value }))}
                  placeholder='{"pages": [{"id": "page-1", "title": "...", "elements": [...]}], "settings": {...}}'
                  rows={12}
                  className="font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  See docs/TEMPLATE_BUILDER_GUIDE.md for form structure with pages, elements, settings, logic, and fieldMappings.
                </p>
              </div>
            )}
            {templateForm.type === 'email' && (
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Email Content (JSON)</label>
                <Textarea
                  value={templateForm.contentJson}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, contentJson: e.target.value }))}
                  placeholder='{"subject": {"en": "..."}, "htmlContent": {"en": "..."}, "textContent": {"en": "..."}, "variables": [...]}'
                  rows={12}
                  className="font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  See docs/TEMPLATE_BUILDER_GUIDE.md for email structure with subject, htmlContent, textContent, and variables.
                </p>
              </div>
            )}
            {templateForm.id && (
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Change Notes</label>
                <Textarea
                  value={templateForm.changeNotes}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, changeNotes: e.target.value }))}
                  placeholder="Describe what changed in this version"
                  rows={2}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setTemplateFormOpen(false);
              setTemplateOriginalState(null);
            }}>Cancel</Button>
            <Button
              onClick={async () => {
                setTemplateSaving(true);
                try {
                  if (!templateForm.slug && !templateForm.id) {
                    toast.error('Slug is required');
                    return;
                  }
                  if (!templateForm.title) {
                    toast.error('Title is required');
                    return;
                  }

                  let content = {};
                  
                  if (templateForm.type === 'task') {
                    // Task templates have steps (step-by-step processes)
                    let steps;
                    try {
                      steps = JSON.parse(templateForm.stepsJson || '[]');
                    } catch (e) {
                      toast.error('Invalid JSON in steps field');
                      return;
                    }
                    content = {
                      estimatedDuration: templateForm.estimatedDuration,
                      steps
                    };
                  } else {
                    // Workflow, form, and email templates use contentJson
                    try {
                      content = JSON.parse(templateForm.contentJson || '{}');
                    } catch (e) {
                      toast.error(`Invalid JSON in ${templateForm.type} content field`);
                      return;
                    }
                  }

                  const tags = templateForm.tags
                    ? templateForm.tags.split(',').map(t => t.trim()).filter(Boolean)
                    : [];

                  const payload = {
                    type: templateForm.type,
                    slug: templateForm.slug,
                    title: templateForm.title,
                    description: templateForm.description,
                    category: templateForm.category,
                    tags,
                    content,
                    changeNotes: templateForm.changeNotes
                  };

                  if (templateForm.id) {
                    await adminSystemAPI.updateTemplate(templateForm.id, payload);
                    toast.success('Template updated');
                    // Reset original state after successful update
                    setTemplateOriginalState(null);
                    // Update the form state to reflect the saved state
                    setTemplateForm(prev => ({ ...prev, ...payload, changeNotes: '' }));
                  } else {
                    await adminSystemAPI.createTemplate(payload);
                    toast.success('Template created');
                  }
                  setTemplateFormOpen(false);
                  loadTemplates();
                } catch (err) {
                  console.error('Template save error:', err);
                  toast.error(err?.response?.data?.message || 'Failed to save template');
                } finally {
                  setTemplateSaving(false);
                }
              }}
              disabled={templateSaving || !templateForm.title || (!templateForm.slug && !templateForm.id)}
            >
              {templateSaving ? 'Saving...' : templateForm.id ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Template Dialog */}
      <Dialog open={publishState.open} onOpenChange={(open) => setPublishState(prev => ({ ...prev, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish Template</DialogTitle>
            <DialogDescription>
              Publish this template and optionally notify users
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Highlight Label</label>
              <Input
                value={publishState.highlightLabel}
                onChange={(e) => setPublishState(prev => ({ ...prev, highlightLabel: e.target.value }))}
                placeholder="New"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Highlight Duration (days)</label>
              <Input
                type="number"
                value={publishState.highlightDays}
                onChange={(e) => setPublishState(prev => ({ ...prev, highlightDays: parseInt(e.target.value) || 7 }))}
                min={1}
                max={30}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={publishState.notifyUsers}
                onCheckedChange={(checked) => setPublishState(prev => ({ ...prev, notifyUsers: checked }))}
              />
              <label className="text-sm font-medium text-foreground">Send notification to users</label>
            </div>
            {publishState.notifyUsers && (
              <>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Announcement Title</label>
                  <Input
                    value={publishState.announcementTitle}
                    onChange={(e) => setPublishState(prev => ({ ...prev, announcementTitle: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Announcement Message</label>
                  <Textarea
                    value={publishState.announcementMessage}
                    onChange={(e) => setPublishState(prev => ({ ...prev, announcementMessage: e.target.value }))}
                    rows={3}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Severity</label>
                  <select
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={publishState.severity}
                    onChange={(e) => setPublishState(prev => ({ ...prev, severity: e.target.value }))}
                  >
                    <option value="info">Info</option>
                    <option value="success">Success</option>
                    <option value="warning">Warning</option>
                    <option value="error">Error</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">Expires In (days)</label>
                  <Input
                    type="number"
                    value={publishState.expiresInDays}
                    onChange={(e) => setPublishState(prev => ({ ...prev, expiresInDays: parseInt(e.target.value) || 7 }))}
                    min={1}
                    max={90}
                  />
                </div>
              </>
            )}
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Change Notes</label>
              <Textarea
                value={publishState.changeNotes}
                onChange={(e) => setPublishState(prev => ({ ...prev, changeNotes: e.target.value }))}
                placeholder="Describe what's new in this version"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishState(prev => ({ ...prev, open: false }))}>Cancel</Button>
            <Button
              onClick={async () => {
                setTemplateSaving(true);
                try {
                  const expiresAt = publishState.expiresInDays
                    ? new Date(Date.now() + publishState.expiresInDays * 24 * 60 * 60 * 1000)
                    : null;
                  await adminSystemAPI.publishTemplate(publishState.templateId, {
                    highlight: {
                      enabled: true,
                      label: publishState.highlightLabel,
                      highlightUntil: new Date(Date.now() + publishState.highlightDays * 24 * 60 * 60 * 1000)
                    },
                    notifyUsers: publishState.notifyUsers,
                    announcement: publishState.notifyUsers ? {
                      title: publishState.announcementTitle,
                      message: publishState.announcementMessage,
                      severity: publishState.severity,
                      expiresAt
                    } : null,
                    changeNotes: publishState.changeNotes
                  });
                  toast.success('Template published');
                  setPublishState(prev => ({ ...prev, open: false }));
                  loadTemplates();
                } catch (err) {
                  toast.error('Failed to publish template');
                } finally {
                  setTemplateSaving(false);
                }
              }}
              disabled={templateSaving}
            >
              {templateSaving ? 'Publishing...' : 'Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Template Confirmation Dialog */}
      <Dialog open={deleteTemplateDialog.open} onOpenChange={(open) => setDeleteTemplateDialog({ open, templateId: null, templateTitle: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Global Template</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteTemplateDialog.templateTitle}"? This action cannot be undone and will remove the template from all agencies.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTemplateDialog({ open: false, templateId: null, templateTitle: '' })}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                try {
                  await adminSystemAPI.deleteTemplate(deleteTemplateDialog.templateId);
                  toast.success('Template deleted successfully');
                  setDeleteTemplateDialog({ open: false, templateId: null, templateTitle: '' });
                  loadTemplates();
                } catch (err) {
                  console.error('Failed to delete template:', err);
                  toast.error(err?.response?.data?.message || 'Failed to delete template');
                }
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Type Selector Dialog */}
      <Dialog open={templateTypeSelectorOpen} onOpenChange={setTemplateTypeSelectorOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Template with Builder</DialogTitle>
            <DialogDescription>
              Choose a template type and use the visual builder to create it
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start h-auto p-4"
              onClick={() => {
                window.open('/task-templates/new?global=true', '_blank');
                setTemplateTypeSelectorOpen(false);
              }}
            >
              <div className="text-left">
                <div className="font-semibold text-foreground">Workflow / Task Template</div>
                <div className="text-sm text-muted-foreground">Create step-by-step process templates (NIE, TIE, etc.)</div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start h-auto p-4"
              onClick={async () => {
                try {
                  // Prompt for form name using dialog
                  setPromptDialog({
                    open: true,
                    title: 'Form Template Name',
                    message: 'Enter a name for the form template:',
                    defaultValue: `Form Template ${new Date().toLocaleDateString()}`,
                    inputLabel: 'Template Name',
                    onConfirm: async (formName) => {
                      if (!formName || !formName.trim()) {
                        toast.error('Template name is required');
                        return;
                      }
                      
                      try {
                        // Create a new form first, then open builder in global mode
                        const { adminSystemAPI } = await import('../../utils/crmApi');
                        const baseSlug = formName.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || 'form-template';
                        const response = await adminSystemAPI.createTemplate({
                          type: 'form',
                          slug: `${baseSlug}-${Date.now()}`,
                          title: formName.trim(),
                          description: '',
                          category: '',
                          tags: [],
                          content: {
                            pages: [{ id: 'page-1', title: 'Page 1', elements: [] }],
                            settings: { progressBar: true },
                            theme: {},
                            logic: [],
                            mapping: []
                          }
                        });
                        const newId = response.data?.data?._id;
                        if (newId) {
                          window.open(`/forms/${newId}/builder?global=true`, '_blank');
                        } else {
                          toast.error('Failed to create form template');
                        }
                        setTemplateTypeSelectorOpen(false);
                        setPromptDialog({ open: false, title: '', message: '', defaultValue: '', onConfirm: null, inputLabel: 'Value' });
                      } catch (err) {
                        console.error('Failed to create form template:', err);
                        toast.error('Failed to create form template');
                        setPromptDialog({ open: false, title: '', message: '', defaultValue: '', onConfirm: null, inputLabel: 'Value' });
                      }
                    },
                    onCancel: () => {
                      setTemplateTypeSelectorOpen(false);
                      setPromptDialog({ open: false, title: '', message: '', defaultValue: '', onConfirm: null, inputLabel: 'Value' });
                    }
                  });
                } catch (err) {
                  console.error('Failed to open prompt dialog:', err);
                  toast.error('Failed to open dialog');
                }
              }}
            >
              <div className="text-left">
                <div className="font-semibold text-foreground">Form Template</div>
                <div className="text-sm text-muted-foreground">Create dynamic forms with fields and validation</div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start h-auto p-4"
              onClick={() => {
                window.open('/workflows/visual?global=true', '_blank');
                setTemplateTypeSelectorOpen(false);
              }}
            >
              <div className="text-left">
                <div className="font-semibold text-foreground">Workflow (Visual Builder)</div>
                <div className="text-sm text-muted-foreground">Create automated workflows with visual drag-and-drop builder</div>
              </div>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateTypeSelectorOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Announcement Form Dialog */}
      <Dialog open={announcementFormOpen} onOpenChange={(open) => {
        if (!open) {
          setAnnouncementFormOpen(false);
          setAnnouncementDraft({ title: '', message: '', severity: 'info', expiresInDays: 7 });
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Announcement</DialogTitle>
            <DialogDescription>
              Send a notification announcement to all users
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Title</label>
              <Input
                value={announcementDraft.title}
                onChange={(e) => setAnnouncementDraft(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Announcement title"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Message</label>
              <Textarea
                value={announcementDraft.message}
                onChange={(e) => setAnnouncementDraft(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Announcement message"
                rows={4}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Severity</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={announcementDraft.severity}
                onChange={(e) => setAnnouncementDraft(prev => ({ ...prev, severity: e.target.value }))}
              >
                <option value="info">Info</option>
                <option value="success">Success</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Expires In (days)</label>
              <Input
                type="number"
                value={announcementDraft.expiresInDays}
                onChange={(e) => setAnnouncementDraft(prev => ({ ...prev, expiresInDays: parseInt(e.target.value) || 7 }))}
                min={1}
                max={90}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setAnnouncementFormOpen(false);
              setAnnouncementDraft({ title: '', message: '', severity: 'info', expiresInDays: 7 });
            }}>Cancel</Button>
            <Button
              onClick={async () => {
                setAnnouncementSaving(true);
                try {
                  const expiresAt = announcementDraft.expiresInDays
                    ? new Date(Date.now() + announcementDraft.expiresInDays * 24 * 60 * 60 * 1000)
                    : null;
                  await adminSystemAPI.createAnnouncement({
                    title: announcementDraft.title,
                    message: announcementDraft.message,
                    severity: announcementDraft.severity,
                    expiresAt,
                    publishedAt: new Date()
                  });
                  toast.success('Announcement created');
                  setAnnouncementFormOpen(false);
                  setAnnouncementDraft({ title: '', message: '', severity: 'info', expiresInDays: 7 });
                  loadAnnouncements();
                } catch (err) {
                  toast.error('Failed to create announcement');
                } finally {
                  setAnnouncementSaving(false);
                }
              }}
              disabled={announcementSaving || !announcementDraft.title || !announcementDraft.message}
            >
              {announcementSaving ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => {
        if (!open) {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, confirmLabel: 'Confirm', variant: 'default' });
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{confirmDialog.title}</DialogTitle>
            <DialogDescription>{confirmDialog.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              if (confirmDialog.onCancel) {
                confirmDialog.onCancel();
              } else {
                setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, confirmLabel: 'Confirm', variant: 'default' });
              }
            }}>
              Cancel
            </Button>
            <Button
              variant={confirmDialog.variant === 'destructive' ? 'destructive' : 'default'}
              onClick={() => {
                if (confirmDialog.onConfirm) {
                  confirmDialog.onConfirm();
                }
              }}
            >
              {confirmDialog.confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prompt Dialog */}
      <Dialog open={promptDialog.open} onOpenChange={(open) => {
        if (!open) {
          setPromptDialog({ open: false, title: '', message: '', defaultValue: '', onConfirm: null, inputLabel: 'Value' });
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{promptDialog.title}</DialogTitle>
            <DialogDescription>{promptDialog.message}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">{promptDialog.inputLabel}</label>
              <Input
                id="prompt-input"
                defaultValue={promptDialog.defaultValue}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && promptDialog.onConfirm) {
                    const input = document.getElementById('prompt-input');
                    promptDialog.onConfirm(input?.value || '');
                  }
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              if (promptDialog.onCancel) {
                promptDialog.onCancel();
              } else {
                setPromptDialog({ open: false, title: '', message: '', defaultValue: '', onConfirm: null, inputLabel: 'Value' });
              }
            }}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const input = document.getElementById('prompt-input');
                if (promptDialog.onConfirm) {
                  promptDialog.onConfirm(input?.value || '');
                }
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


